import React, { useState } from 'react';
import { pursuitApi, documentsApi } from '../../api/client';

// Seller pursuit: Proposal Response. Drafts a proposal volume section-by-section
// (each a fast call) grounded in the capture plan + compliance matrix + documents,
// assembles an editable draft, and saves it as a versioned document. Advisory.

interface Props { oppId: string; opp?: any; }

const VOLUMES: { id: string; label: string }[] = [
  { id: 'technical', label: 'Technical Volume' },
  { id: 'past_performance', label: 'Past Performance Volume' },
  { id: 'management', label: 'Management Volume' },
  { id: 'price_narrative', label: 'Price Narrative' },
];

export default function ProposalResponseTab({ oppId, opp }: Props) {
  const [volume, setVolume] = useState('technical');
  const [drafting, setDrafting] = useState(false);
  const [progress, setProgress] = useState('');
  const [draft, setDraft] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [format, setFormat] = useState<'docx' | 'pdf'>('docx');

  const volumeLabel = VOLUMES.find(v => v.id === volume)?.label || volume;

  const generate = async () => {
    setDrafting(true); setError(''); setMsg(''); setDraft(''); setProgress('');
    try {
      // 1) Get the section list (instant, no AI).
      const plan = await pursuitApi.getProposalSections(oppId, volume);
      const sections: string[] = plan.sections || [];
      if (sections.length === 0) { setError('No sections defined for this volume.'); setDrafting(false); return; }

      // 2) Draft each section sequentially (each call is fast; stays under the API limit).
      const parts: string[] = [`${plan.volume_label || volumeLabel}`];
      for (let i = 0; i < sections.length; i++) {
        setProgress(`Drafting ${i + 1}/${sections.length}: ${sections[i]}…`);
        const res = await pursuitApi.draftProposalSection(oppId, volume, i);
        const title = res.section_title || sections[i];
        const body = (res.text || '').trim();
        parts.push(`\n\n${title}\n\n${body}`);
        // Show progressive output as it builds.
        setDraft(parts.join(''));
      }
      setProgress('');
      setDirty(true);
      setMsg('Draft assembled. Review and edit, then Save as a document.');
    } catch (e: any) {
      setError(e?.message || 'Draft failed.');
    }
    setDrafting(false);
  };

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true); setError(''); setMsg('');
    try {
      const title = `${volumeLabel} — ${opp?.title || 'Pursuit'}`;
      await documentsApi.saveDraft(oppId, `proposal_${volume}`, title, draft, format, {
        opportunity: opp?.title,
        agency: opp?.agency,
      });
      setDirty(false);
      setMsg(`Saved as ${format.toUpperCase()} to the Documents panel.`);
    } catch (e: any) {
      setError(e?.message || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 940 }}>
      <h2 style={{ fontSize: 18, margin: 0 }}>Proposal Response</h2>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 16px', maxWidth: 680 }}>
        Draft a proposal volume grounded in your Capture Plan, the Compliance Matrix, and the
        solicitation. Sections are drafted one at a time, then assembled into an editable draft you
        can refine and save as a document. Advisory — always review before submission.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#475569', fontWeight: 600 }}>
          Volume
          <select value={volume} onChange={e => setVolume(e.target.value)} disabled={drafting}
            style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, minWidth: 210 }}>
            {VOLUMES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </label>
        <button onClick={generate} disabled={drafting}
          style={{ padding: '10px 18px', background: drafting ? '#93c5fd' : '#4338ca', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: drafting ? 'default' : 'pointer' }}>
          {drafting ? 'Drafting…' : draft ? 'Re-draft with AI' : 'Draft with AI'}
        </button>
        {progress && <span style={{ fontSize: 12, color: '#6b7280' }}>{progress}</span>}
      </div>

      {error && <div role="alert" style={{ color: '#991b1b', background: '#fee2e2', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {msg && <div style={{ color: '#166534', background: '#dcfce7', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{msg}</div>}

      {draft && (
        <>
          <textarea
            value={draft}
            onChange={e => { setDraft(e.target.value); setDirty(true); }}
            rows={26}
            aria-label="Proposal draft"
            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical', whiteSpace: 'pre-wrap' }}
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
            <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
              Save as{' '}
              <select value={format} onChange={e => setFormat(e.target.value as 'docx' | 'pdf')}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}>
                <option value="docx">Word (.docx)</option>
                <option value="pdf">PDF</option>
              </select>
            </label>
            <button onClick={save} disabled={saving || !dirty}
              style={{ padding: '9px 16px', background: dirty ? '#10b981' : '#e2e8f0', color: dirty ? 'white' : '#94a3b8', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: dirty && !saving ? 'pointer' : 'default' }}>
              {saving ? 'Saving…' : 'Save to Documents'}
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
            Advisory draft. Verify every claim, page limit, and [DRAFT ASSUMPTION] before submission.
          </p>
        </>
      )}
    </div>
  );
}
